<script setup lang="ts">
/* eslint-disable max-lines */
import MiiaFwConfig from '@/configs/config'
import { EMERGENCY_S3_DIRECTORY_NAMES } from '@/constants/s3/directoryName'
import {
  CHUNK_SIZE,
  EMERGENCY_INSPECTION_OPTIONS
} from '@/constants/ui/buildingDetails/buildingDetailsConstants'
import { useMiiaFwS3 } from '@/features/miiaFw/composables/useMiiaFwS3'
import { chunkArray } from '@/utils/arrayUtil/arrayUtils'
import { computed, reactive, ref, watch } from 'vue'
import { NO_IMAGE_MESSAGE } from '@/constants/ui/buildingDetails/buildingDetailsConstants'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import { MARKER_TYPE } from '@/constants/ui/common/commonUiConstants'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useBuildingDetail } from '@/composables/buildingDetails/useBuildingDetails'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { useZoomBuildingImage } from '@/composables/buildingDetails/useZoomBuildingImage'
import ZoomBuildingImage from './ZoomBuildingImage.vue'

const miiaFwS3 = useMiiaFwS3()
const { setLoadingView } = useLoadingView()
const buildingDetail = useBuildingDetail()
const { includesFeatureOptionsEmergency } = useGovernmentSettingStore()
const zoomBuildingImage = useZoomBuildingImage()

/** 選択中の調査項目キー（応急危険度判定） */
const selectedKey = ref(EMERGENCY_INSPECTION_OPTIONS[0].key)

/** アコーディオン表示部分の開閉フラグ */
const isAccordionOpen = reactive({
  emergencyAssessment:
    buildingDetail.selectedMarkerType.value === MARKER_TYPE.residentialDamage ||
    buildingDetail.selectedMarkerType.value === MARKER_TYPE.emergency,
  aiCollapseAssessment: buildingDetail.selectedMarkerType.value === MARKER_TYPE.aiCollapse
})

/** 画像表示用カルーセルの表示位置（応急危険度判定） */
const emergencyImageCurrentPage = ref<number>(0)

/** 画像表示用カルーセルの表示位置（AI倒壊判定） */
const aiCollapseImageCurrentPage = ref<number>(0)

/** 応急危険度判定の画像URLリスト */
const emergencyImageUrlList = ref<string[]>([])

/** AI倒壊判定の画像URLリスト */
const aiCollapseImageUrlList = ref<string[]>([])

/** ページごとに分割した画像URL（応急危険度判定） */
const emergencyImageUrlChunks = computed(() => chunkArray(emergencyImageUrlList.value, CHUNK_SIZE))

/** 画像表示カルーセルのページ数（応急危険度判定） */
const emergencyImageMaxPage = computed(() =>
  emergencyImageUrlChunks.value.length === 0 ? 0 : emergencyImageUrlChunks.value.length - 1
)

/** ページごとに分割した画像URL（AI倒壊判定） */
const aiCollapseImageUrlChunks = computed(() =>
  chunkArray(aiCollapseImageUrlList.value, CHUNK_SIZE)
)

/** 画像表示カルーセルのページ数（AI倒壊判定） */
const aiCollapseImageMaxPage = computed(() =>
  aiCollapseImageUrlChunks.value.length === 0 ? 0 : aiCollapseImageUrlChunks.value.length - 1
)

/**
 * アコーディオンセクションの開閉状態を切り替える
 * @param section 切り替え対象セクション
 * @returns なし
 */
const toggleSection = (section: keyof typeof isAccordionOpen): void => {
  isAccordionOpen[section] = !isAccordionOpen[section]
}

/** 画像表示カルーセル上で前ページに遷移する（応急危険度判定） */
const emergencyImagePrevPage = (): void => {
  if (emergencyImageCurrentPage.value > 0) emergencyImageCurrentPage.value--
}

/** 画像表示カルーセル上で次ページに遷移する（応急危険度判定） */
const emergencyImageNextPage = (): void => {
  if (emergencyImageCurrentPage.value < emergencyImageMaxPage.value)
    emergencyImageCurrentPage.value++
}

/** 画像表示カルーセル上で前ページに遷移する（AI倒壊判定） */
const aiCollapseImagePrevPage = (): void => {
  if (aiCollapseImageCurrentPage.value > 0) aiCollapseImageCurrentPage.value--
}

/** 画像表示カルーセル上で次ページに遷移する（AI倒壊判定） */
const aiCollapseImageNextPage = (): void => {
  if (aiCollapseImageCurrentPage.value < aiCollapseImageMaxPage.value)
    aiCollapseImageCurrentPage.value++
}

/**
 * S3から画像を取得し、画像URLリストを作成する
 * @param bucketName 取得先バケット名
 * @param directory 取得先ディレクトリ
 * @param imageList 取得対象ファイル名リスト
 * @returns 画像URLリスト
 */
const createImageUrlList = async (
  bucketName: string,
  directory: string | null,
  imageList: string[] | null
): Promise<string[]> => {
  if (!directory || !imageList) return []
  try {
    setLoadingView(true)

    const results = await Promise.all(
      imageList.map(async e => {
        const bucketParam = {
          name: bucketName,
          prefix: '',
          delimiter: '/'
        }
        const filePath = `${directory}/${e}`

        try {
          const result = await miiaFwS3.downloadObject(bucketParam, filePath, 'blob')

          const file = new File([result], filePath, { type: result.type })
          return URL.createObjectURL(file)
        } catch (err) {
          console.error(err)
          throw new GlobalError([ERROR_CODES.IMAGE_DOWNLOAD_FAILED])
        }
      })
    )

    return results
  } finally {
    setLoadingView(false)
  }
}

/**
 * 指定されたリストの画像URLのリソースを解放する
 * @param urlList URLリスト
 * @returns なし
 */
const revokeObjectUrl = (urlList: string[]) => {
  urlList.forEach(url => {
    if (url !== undefined) {
      URL.revokeObjectURL(url)
    }
  })
}

/**
 * 画像クリック
 * */
const handleImageClick = (imageUrl: string): void => {
  zoomBuildingImage.close()
  zoomBuildingImage.open(imageUrl)
}

/**
 * コンポーネントを閉じる
 * @param なし
 * @returns なし
 */
const close = (): void => {
  revokeObjectUrl(emergencyImageUrlList.value)
  revokeObjectUrl(aiCollapseImageUrlList.value)
  buildingDetail.close()
}

// セレクトボックスの入力内容変更を検知し、表示する画像を更新する
watch(selectedKey, async newValue => {
  revokeObjectUrl(emergencyImageUrlList.value)

  // newValue に対応した画像ファイル名の配列を取得（存在しなければ空配列）
  const imageListForKey = buildingDetail.emergencyImageList.value?.[newValue] ?? []

  emergencyImageUrlList.value = await createImageUrlList(
    MiiaFwConfig.config.s3Access.contentsBucketEmergency,
    `${buildingDetail.emergencyImageDirectory.value}/${newValue}`,
    imageListForKey
  )
})

// 表示・非表示の切り替え
watch(
  () => buildingDetail.isOpen.value,
  async newValue => {
    if (newValue) {
      // 新規で表示されたときの処理
      // 応急危険度判定の画像URLを作成
      emergencyImageUrlList.value = await createImageUrlList(
        MiiaFwConfig.config.s3Access.contentsBucketEmergency,
        `${buildingDetail.emergencyImageDirectory.value}/${EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR}`, // 初期表示は「一見して危険かどうか」
        buildingDetail.emergencyImageList.value?.exterior ?? []
      )
      // AI倒壊判定の画像URL作成
      aiCollapseImageUrlList.value = await createImageUrlList(
        MiiaFwConfig.config.s3Access.contentsBucketScreeningGeoResult,
        buildingDetail.aiCollapseImageDirectory.value,
        buildingDetail.aiCollapseImageList.value
      )
    } else {
      // 閉じる処理
      close()
    }
  }
)

// 選択されたマーカーの種別によってアコーディオンセクションの表示状態を更新
watch(
  () => buildingDetail.selectedMarkerType.value,
  newMarkerType => {
    isAccordionOpen.emergencyAssessment =
      newMarkerType === MARKER_TYPE.residentialDamage || newMarkerType === MARKER_TYPE.emergency
    isAccordionOpen.aiCollapseAssessment = newMarkerType === MARKER_TYPE.aiCollapse
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="buildingDetail.isOpen.value">
    <div class="building-details">
      <!-- ヘッダー -->
      <header class="header">
        建造物詳細
        <button class="close-btn" @click="close">
          <font-awesome-icon icon="fa-xmark" />
        </button>
      </header>

      <!-- アコーディオンセクション -->
      <section class="accordion">
        <!-- 応急危険度判定調査 -->
        <div class="accordion-item" v-if="includesFeatureOptionsEmergency()">
          <button class="accordion-header" @click="toggleSection('emergencyAssessment')">
            <div v-if="isAccordionOpen.emergencyAssessment">
              <font-awesome-icon icon="fa-caret-down" />
            </div>
            <div v-else>
              <font-awesome-icon icon="fa-caret-right" />
            </div>
            応急危険度判定調査
          </button>
          <div v-if="isAccordionOpen.emergencyAssessment" class="accordion-content">
            <p>
              調査日：{{
                buildingDetail.emergencyInspectionDate.value
                  ? buildingDetail.emergencyInspectionDate.value
                  : '未調査'
              }}
            </p>
            <p>
              判定結果：{{
                buildingDetail.emergencyInspectionResult.value
                  ? buildingDetail.emergencyInspectionResult.value
                  : 'ー'
              }}
            </p>
            <div class="inspection-select-wrapper">
              <label class="inspection-label" for="inspectionSelect">撮影画像：</label>
              <b-form-select
                id="inspectionSelect"
                v-model="selectedKey"
                :options="
                  buildingDetail.filteredEmergencyInspectionOptions.value.map(o => ({
                    value: o.key,
                    text: o.label
                  }))
                "
                size="sm"
                class="custom-select"
              />
            </div>
            <div class="carousel-wrapper">
              <button
                class="nav-btn prev-btn"
                @click="emergencyImagePrevPage"
                :disabled="emergencyImageCurrentPage === 0"
              >
                <font-awesome-icon icon="fa-caret-left" />
              </button>

              <div class="carousel-container">
                <div
                  v-if="
                    emergencyImageUrlChunks[emergencyImageCurrentPage] &&
                    emergencyImageUrlChunks[emergencyImageCurrentPage].length > 0
                  "
                  class="image-grid"
                >
                  <img
                    v-for="(img, idx) in emergencyImageUrlChunks[emergencyImageCurrentPage]"
                    @click="handleImageClick(img)"
                    :key="idx"
                    :src="img"
                    :alt="`${idx}`"
                    class="image-item"
                  />
                </div>
                <div v-else class="no-images">{{ NO_IMAGE_MESSAGE }}</div>
              </div>

              <button
                class="nav-btn next-btn"
                @click="emergencyImageNextPage"
                :disabled="emergencyImageCurrentPage === emergencyImageMaxPage"
              >
                <font-awesome-icon icon="fa-caret-right" />
              </button>
            </div>
          </div>
        </div>
        <!-- AI倒壊判定 -->
        <div class="accordion-item">
          <button class="accordion-header" @click="toggleSection('aiCollapseAssessment')">
            <div v-if="isAccordionOpen.aiCollapseAssessment">
              <font-awesome-icon icon="fa-caret-down" />
            </div>
            <div v-else>
              <font-awesome-icon icon="fa-caret-right" />
            </div>
            AI倒壊判定
          </button>
          <div v-if="isAccordionOpen.aiCollapseAssessment" class="accordion-content">
            <p>
              撮影日：{{
                buildingDetail.aiCollapseAssessmentDate.value
                  ? buildingDetail.aiCollapseAssessmentDate.value
                  : '未判定'
              }}
            </p>
            <p>
              判定結果：{{
                buildingDetail.aiCollapseAssessmentResult.value
                  ? buildingDetail.aiCollapseAssessmentResult.value
                  : 'ー'
              }}
            </p>
            <p>空撮画像</p>

            <div class="carousel-wrapper">
              <button
                class="nav-btn prev-btn"
                @click="aiCollapseImagePrevPage"
                :disabled="aiCollapseImageCurrentPage === 0"
              >
                <font-awesome-icon icon="fa-caret-left" />
              </button>

              <div class="carousel-container">
                <div
                  v-if="
                    aiCollapseImageUrlChunks[aiCollapseImageCurrentPage] &&
                    aiCollapseImageUrlChunks[aiCollapseImageCurrentPage].length > 0
                  "
                  class="image-grid"
                >
                  <img
                    v-for="(img, idx) in aiCollapseImageUrlChunks[aiCollapseImageCurrentPage]"
                    @click="handleImageClick(img)"
                    :key="idx"
                    :src="img"
                    :alt="`${idx}`"
                    class="image-item"
                  />
                </div>
                <div v-else class="no-images">{{ NO_IMAGE_MESSAGE }}</div>
              </div>
              <button
                class="nav-btn next-btn"
                @click="aiCollapseImageNextPage"
                :disabled="aiCollapseImageCurrentPage === aiCollapseImageMaxPage"
              >
                <font-awesome-icon icon="fa-caret-right" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
    <ZoomBuildingImage class="zoom-image-modal-wrapper"></ZoomBuildingImage>
  </div>
</template>

<style scoped>
.building-details {
  width: 450px;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  background: #f9f9f9;
  border-radius: 6px;
}

.header {
  position: relative;
  padding: 10px;
  background: #ddd;
  font-weight: bold;
  font-size: 16px;
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
}

.accordion {
  flex-grow: 1;
  overflow-y: auto;
  font-size: 16px;
}

.accordion-item + .accordion-item {
  border-top: 1px solid #bbb;
}

.accordion-header {
  width: 100%;
  padding: 10px;
  background: #eee;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.accordion-content {
  padding: 10px;
  background: #fff;
}

.carousel-wrapper {
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  user-select: none;
  padding: 0;
  min-height: 160px;
  box-sizing: border-box;
  overflow: hidden;
  margin-top: 10px;
}

.carousel-container {
  min-height: 160px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 12px;
  width: max-content;
  margin: 0;
  padding: 0;
}

.image-item {
  width: 160px;
  height: 100px;
  object-fit: contain;
  border: 1px solid #ccc;
  display: block;
  margin: 0;
}

.no-images {
  width: 320px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #666;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  font-size: 1rem;
  user-select: none;
  box-sizing: border-box;
  margin: auto;
}

.inspection-select-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  width: 100%;
  max-width: 450px;
  box-sizing: border-box;
}

.inspection-label {
  width: 80px;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: center;
}

.custom-select {
  font-size: 14px;
  height: auto;
}

.nav-btn {
  background-color: white;
  color: #424242;
  border: none;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  font-size: 50px;
  transform: scaleY(2.5);
}

.nav-btn:disabled {
  background-color: transparent;
  color: rgba(42, 42, 42, 0.5);
  cursor: not-allowed;
}
.zoom-image-modal-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 20;
  background-color: rgba(0, 0, 0, 0.5);
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
